import React, {useEffect, useState} from 'react';
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import Modal from "../components/Modal";
import moment from "moment/moment";
import {
    getComponentColor,
    getConfig,
    getCurrentBooking,
    getExtendModalVisibility,
    getNextBooking,
    setActionAuthentication, setCurrentBooking,
    setError,
    setExtendModalVisibility,
    setSuccess
} from "../reducers/configSlice";
import {Icon} from 'react-native-elements';
import {formatSharpHour, getDaySchedule} from "../services/helpers/dateTimeHelpers";
import {apiAuthorizationToken, apiUpdateBooking} from "../api/actions";
import {EXTEND_ACTION, ICON_EXTEND_MODAL} from "../constants/default";
import moment_tz from "moment-timezone";
import {emitFutureBookingList} from "../services/socket/socket";
import {addActionAuthenticationListener} from "../services/actionAuthentication/actionAuthenticationService";
import {FONT_FAMILY_BOLDER, FONT_FAMILY_LIGHT} from "../constants/styleConstants";
import { Ionicons } from '@expo/vector-icons';

import {actuatedNormalize} from "../services/helpers/stylingHelpers";

const ExtendModal = () => {
    const dispatch = useDispatch()
    const config = useSelector(getConfig)
    const extendModalVisible = useSelector(getExtendModalVisibility)
    const currentBooking = useSelector(getCurrentBooking)
    const nextBooking = useSelector(getNextBooking)
    const componentColor = useSelector(getComponentColor)
    const [availablePeriods, setAvailablePeriods] = useState([15, 30, 60])

    const [timeBetweenBookings, setTimeBetweenBookings] = useState(0)
    const [extendPeriod, setExtendPeriod] = useState(0)

    const getMaxAvailableEndTime = () => {
        if (nextBooking) {
            return moment_tz.tz(nextBooking.start, config.timezone).utc().tz(config.timezone)
        }

        return moment_tz.tz(moment().format('YYYY-MM-DD') + " " + formatSharpHour(getDaySchedule().end), config.timezone).utc().tz(config.timezone)
    }

    useEffect( () => {
        if (config.granularity <= 15) {
            return;
        }

        setAvailablePeriods([config.granularity, config.granularity * 2, config.granularity * 3])
    }, [config])

    useEffect(() => {
        if (!currentBooking) {
            return;
        }

        const currentBookingEnd = moment_tz.tz(currentBooking.end, config.timezone).utc().tz(config.timezone)
        const maxAvailableEndTime = getMaxAvailableEndTime()
        setTimeBetweenBookings(parseInt(maxAvailableEndTime.diff(currentBookingEnd, 'minutes')))
    }, [currentBooking, nextBooking])

    const extendBooking = async () => {
        if (!extendPeriod || !currentBooking) {
            return;
        }

        const bookingId = currentBooking.booking_id;
        const bookingEnd =  moment(currentBooking.end)
        const newEnd = bookingEnd.add(extendPeriod, 'minutes').format('YYYY-MM-DD HH:mm')

        let data = {
            dates: [{
                start: currentBooking.start,
                end: newEnd
            }],
            terms_and_conditions: 1
        }

        if (currentBooking.other_dates && currentBooking.other_dates.length) {
            currentBooking.other_dates.forEach( (date, index) => {
                data.dates.push({
                    start: date.start,
                    end: date.end
                })
            })
        }

        if (config.require_login_actions) {
            const result = await apiAuthorizationToken(EXTEND_ACTION, {...data, booking_id: bookingId})

            if (result.status !== 1 || !result.code || !result.data) {
                return;
            }

            closeModal()

            const authAction = {
                action: EXTEND_ACTION,
                successMessage: translate("Booking successfully extended"),
                link: result.data,
                code: result.code
            }

            await addActionAuthenticationListener(authAction, closeModal)

            setTimeout( () => {
                dispatch(setActionAuthentication(authAction))
            }, 500)

            return;
        }

        const { status, error } = await apiUpdateBooking(bookingId, data)

        if (status !== 1) {
            dispatch(setError(error.error))
            closeModal()
        }

        dispatch(setSuccess(translate('Booking successfully extended')))
        dispatch(setCurrentBooking({
            ...currentBooking,
            end: newEnd
        }))

        emitFutureBookingList()
        closeModal()
    }

    const closeModal = () => {
        setExtendPeriod(0)
        dispatch(setExtendModalVisibility(false))
    };

    const getNewEndTime = (amount, unit) => {
        let bookingEnd = currentBooking ? currentBooking.end : null;

        return bookingEnd &&
            moment(bookingEnd).add(amount, unit).format(config.time_format === "ampm" ? 'h:mm A' : "HH:mm")
    };

    const renderPeriod = (period, index) => {
        const isHour = (period % 60 === 0)
        const text = ((isHour ? period / 60 : period) + " " + (isHour ? (period / 60 === 1 ? translate("hour") : translate("hours")) : translate("minutes")))
        const selected = (extendPeriod === period)
        const disabled = (timeBetweenBookings - period) < 0

        const borderColor = Color(componentColor).rotate(20).rgb().string()

        return (
            <TouchableOpacity
                key={index} onPress={() => setExtendPeriod(period)}
                disabled={!!disabled} style={[
                    {borderBottomColor: borderColor},
                    styles.period,
                    selected && styles.periodSelected,
                    disabled && styles.periodDisabled,
                ]}
            >
                <View style={styles.periodWrapper}>
                    <Ionicons
                        size={actuatedNormalize(selected ? 32 : 30)}
                        name={"checkmark-circle" + (selected ? "" : "-outline")}
                        color={selected ? componentColor : "#5DB18A"}
                    />
                    <Text style={styles.periodTime}>
                        {text}
                    </Text>
                </View>
                <Text style={styles.periodInfo}>
                    {translate("New end time will be %s", [getNewEndTime(period, 'minutes')])}
                </Text>
            </TouchableOpacity>
        );
    }

    return (
        <Modal visible={extendModalVisible}
               onClose={closeModal}
               icon={ICON_EXTEND_MODAL}
               title={translate("Extend the booking")}
               subtitle={translate("Choose an extension time from below")}
               disabled={!extendPeriod}
               buttonText={translate("Extend").toUpperCase()}
               onButtonPress={extendBooking}
        >
            <View style={styles.extendPeriodsWrapper}>
                {availablePeriods.map(renderPeriod)}
            </View>
        </Modal>
    )
}

const styles = StyleSheet.create({
    extendPeriodsWrapper: {
        flex: 0.42,
        backgroundColor: '#F8F9FA'
    },
    period: {
        flex: 0.33,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems:'center',
        backgroundColor: '#FFFFFF',
        paddingLeft: '11.3%',
        paddingRight: '11.3%',
        borderTopWidth: 1,
        borderTopColor:'#E4EAEC',
        borderBottomWidth: 0,
    },
    periodSelected: {
        flex: 0.336,
        paddingLeft: '12.3%',
        paddingRight: '12.3%',
        borderBottomWidth: 2,
        transform: [{scale: 1.025}]
    },
    periodDisabled: {
        backgroundColor: '#F8F9FA',
        opacity: 0.2
    },
    periodWrapper: {
        flexDirection: 'row',
        alignItems: 'center'
    },
    periodTime: {
        fontSize: actuatedNormalize(14),
        fontFamily: FONT_FAMILY_BOLDER,
        marginLeft: actuatedNormalize(12),
    },
    periodInfo: {
        fontFamily: FONT_FAMILY_LIGHT,
        color: '#5DB18A',
        fontSize: actuatedNormalize(10)
    },
});

export default ExtendModal
